import { PropsWithChildren } from 'react';

import { Box, Button } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { TEAM_QUANTITY_MIN } from '~/features/checkout/constants';
import { BillingFrequency } from '~/features/checkout/models/checkout';
import { useUser } from '~/features/user/hooks/use-user';
import { useClientRender } from '~/hooks/use-client-render';

import { PlanId } from '../models/plan-metadata';

export type SignUpButtonProps = PropsWithChildren<{
  billingFrequency: BillingFrequency;
  planId: PlanId;
  overrideLabel?: string;
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: () => void;
  programKey?: string;
  learningPlanKey?: string;
  colorScheme?: 'blue' | 'lime' | 'purple';
}>;

const buttonStyleProps = {
  size: 'icon-right',
  rightIcon: <ArrowRightIcon h='24px' w='24px' />,
  width: '100%',
};

function SignUpButtonBase({
  overrideLabel,
  ariaLabel,
  href,
  disabled,
  onClick,
  children,
  colorScheme = 'blue',
}: Pick<SignUpButtonProps, 'overrideLabel' | 'ariaLabel' | 'disabled' | 'onClick' | 'children' | 'colorScheme'> & {
  href: string;
}) {
  const label = overrideLabel ?? 'Enroll Now';
  return (
    <Box>
      <ButtonLink
        buttonProps={{
          ...buttonStyleProps,
          'aria-label': ariaLabel,
          isDisabled: disabled,
          onClick,
          ...(colorScheme === 'lime' && {
            backgroundColor: 'accent.lime',
            colorScheme: 'sea-foam',
            _hover: {
              backgroundColor: '#ddff56',
            },
          }),
          ...(colorScheme === 'purple' && {
            backgroundColor: 'purple.700',
            color: 'white',
            _hover: {
              backgroundColor: 'purple.500',
            },
          }),
        }}
        linkProps={{
          href,
        }}
      >
        {children || label}
      </ButtonLink>
    </Box>
  );
}

function getCommonSearchParams(props: SignUpButtonProps): URLSearchParams {
  const search = new URLSearchParams();
  if (props.planId === 'individual' && props.billingFrequency === 'purchase') {
    if (props.programKey) {
      search.set('nodeKey', props.programKey);
    }
    if (props.learningPlanKey) {
      search.set('learningPlanKey', props.learningPlanKey);
    }
  }
  return search;
}

export function UnauthenticatedSignUpButton(props: SignUpButtonProps) {
  const { overrideLabel, planId, billingFrequency } = props;
  const search = getCommonSearchParams(props);
  const params = search.toString();
  const label = overrideLabel ?? 'Enroll Now';
  return (
    <SignUpButtonBase
      {...props}
      disabled={props?.disabled}
      href={`/checkout/${planId}/${billingFrequency}/credit-card${params ? `?${params}` : ''}`}
    >
      {label}
    </SignUpButtonBase>
  );
}

export function AuthenticatedSignUpButton(props: SignUpButtonProps) {
  const { overrideLabel, planId, billingFrequency, programKey, learningPlanKey } = props;

  const search = getCommonSearchParams(props);
  if (planId.includes('team')) {
    search.set('quantity', String(TEAM_QUANTITY_MIN));
  }
  const params = search.toString();
  const label = overrideLabel ?? 'Enroll Now';

  return (
    <SignUpButtonBase
      {...props}
      href={`/checkout/${planId}/${billingFrequency}/credit-card${params ? `?${params}` : ''}`}
      disabled={
        props?.disabled ||
        (planId === 'individual' && billingFrequency === 'purchase' && !programKey && !learningPlanKey)
      }
    >
      {label}
    </SignUpButtonBase>
  );
}

export function SignUpButton(props: SignUpButtonProps) {
  const { isUserAuthenticated } = useUser();
  const clientRender = useClientRender();
  const label = props.overrideLabel ?? 'Enroll Now';

  if (!clientRender) {
    return (
      <Button {...buttonStyleProps} isDisabled isLoading>
        {label}
      </Button>
    );
  }

  return isUserAuthenticated ? <AuthenticatedSignUpButton {...props} /> : <UnauthenticatedSignUpButton {...props} />;
}

export function IndividualSignUpButton(props: Omit<SignUpButtonProps, 'planId'> & { analyticsEventName?: string }) {
  function handleClick() {
    props?.onClick?.();
    if (props.analyticsEventName) {
      trackEvent({
        name: props.analyticsEventName,
        type: 'click',
        label: 'Individual',
        value: props.billingFrequency,
      });
    }
  }
  return <SignUpButton {...props} planId='individual' onClick={handleClick} />;
}

export function TeamSignUpButton(props: Omit<SignUpButtonProps, 'planId'> & { analyticsEventName?: string }) {
  function handleClick() {
    props?.onClick?.();
    if (props.analyticsEventName) {
      trackEvent({
        name: props.analyticsEventName,
        type: 'click',
        label: 'Team',
      });
    }
  }
  return <SignUpButton {...props} planId='team' onClick={handleClick} />;
}
